/**
 * PaymentTransactionService
 * Service for PaymentTransaction entity operations
 * Route: api/payment-transaction
 * Controller: PaymentTransactionController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * PaymentTransactionService
 * Handles payment transaction CRUD operations and transaction-specific queries
 * Maps to /api/payment-transaction endpoint
 */
export class PaymentTransactionService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PaymentTransactionService.DEFAULT_SERVICE_PATH,
            basePath: PaymentTransactionService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // Override getById to support GUID
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Payment transaction with ID ${id} not found`);
        }
        return response.data;
    }
    getByIdRaw(id) {
        const url = this.buildUrl(id);
        return this.http.get(url);
    }
    // ==================== CACHE INVALIDATION ====================
    /**
     * Invalidate payment transaction cache
     * POST /api/payment-transaction/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.data ?? false;
    }
    /**
     * Invalidate payment transaction cache (raw)
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== MODEL VIEW ENDPOINTS ====================
    /**
     * Get transaction model view by ID
     * GET /api/payment-transaction/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Transaction view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get transaction model view by ID (raw)
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all transaction model views
     * GET /api/payment-transaction/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all transaction model views (raw)
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('view-all', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get transaction views by payer
     * GET /api/payment-transaction/payer/{payerId}/view
     */
    async getViewsByPayer(payerId) {
        const response = await this.getViewsByPayerRaw(payerId);
        return response.data ?? [];
    }
    /**
     * Get transaction views by payer (raw)
     */
    getViewsByPayerRaw(payerId) {
        const endpoint = this.getEndpoint('views-by-payer', `payer/${payerId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get transaction views by status
     * GET /api/payment-transaction/status/{statusId}/view
     */
    async getViewsByStatus(statusId) {
        const response = await this.getViewsByStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get transaction views by status (raw)
     */
    getViewsByStatusRaw(statusId) {
        const endpoint = this.getEndpoint('views-by-status', `status/${statusId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered transaction views with pagination
     * POST /api/payment-transaction/list-view
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered transaction views (raw)
     */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter ?? {});
    }
}
PaymentTransactionService.DEFAULT_SERVICE_PATH = 'payments';
PaymentTransactionService.BASE_PATH = 'api/payment-transaction';
//# sourceMappingURL=PaymentTransactionService.js.map